// Marketing Tarot Deck - 22 Custom Cards
// Inspired by vintage marketing tarot style

export const MARKETING_TAROT_DECK = [
  {
    id: 1,
    name: "The Brand",
    keywords: ["Identity", "Vision", "Purpose"],
    meaning: "Your brand is the torch that lights the way. It represents your core identity, values, and the promise you make to your audience. When drawn, focus on strengthening your brand foundation.",
    reversed: "Brand confusion, inconsistent messaging, lost identity",
    reflection: "What do you want people to feel the moment they encounter your brand? Is your identity aligned with your deepest values?",
    activation: "Write down the one promise your brand makes — and check if every touchpoint keeps it.",
    icon: "torch",
    color: "purple"
  },
  {
    id: 2,
    name: "The Customer",
    keywords: ["Audience", "Understanding", "Empathy"],
    meaning: "The crystal ball reveals deep customer insights. Listen closely to understand their needs, desires, and pain points. Success comes from truly knowing your audience.",
    reversed: "Ignoring feedback, assumptions, disconnection from audience",
    reflection: "When did you last sit down and truly listen to your customers? What are they telling you that you haven't acted on?",
    activation: "Reach out to three customers this week and ask one open-ended question.",
    icon: "crystal",
    color: "teal"
  },
  {
    id: 3,
    name: "The Funnel",
    keywords: ["Journey", "Conversion", "Process"],
    meaning: "Like sand through an hourglass, your funnel guides prospects through their journey. Optimize each stage to turn awareness into action and action into loyalty.",
    reversed: "Leaky funnel, poor conversion, broken customer journey",
    reflection: "Where in your funnel are people dropping off? What would make the next step irresistible for them?",
    activation: "Identify your biggest funnel leak and design one experiment to fix it.",
    icon: "hourglass",
    color: "navy"
  },
  {
    id: 4,
    name: "The Storyteller",
    keywords: ["Narrative", "Content", "Voice"],
    meaning: "The feather and megaphone amplify your message. Craft compelling stories that resonate emotionally. Your narrative is your most powerful marketing tool.",
    reversed: "Weak messaging, uninspiring content, lost voice",
    reflection: "What story are you telling right now — and is it the one your audience needs to hear?",
    activation: "Rewrite your origin story in three sentences that make people feel something.",
    icon: "megaphone",
    color: "teal"
  },
  {
    id: 5,
    name: "The Disruption",
    keywords: ["Innovation", "Change", "Breakthrough"],
    meaning: "Lightning strikes the tower of convention. Embrace bold ideas that shake the market. True disruption creates new categories and redefines possibilities.",
    reversed: "Fear of change, playing it safe, missed opportunities",
    reflection: "What convention in your industry are you afraid to challenge? What would happen if you did?",
    activation: "Pick one 'rule' everyone in your space follows — and break it intentionally.",
    icon: "lightning",
    color: "orange"
  },
  {
    id: 6,
    name: "The Data",
    keywords: ["Analytics", "Insights", "Measurement"],
    meaning: "The charts reveal hidden truths. Let data guide your decisions, but remember to balance numbers with intuition. What gets measured gets managed.",
    reversed: "Analysis paralysis, vanity metrics, ignoring insights",
    reflection: "Are you measuring what matters, or just what's easy to track? What would change if you trusted the numbers?",
    activation: "Pick your single most important metric and build your next decision around it.",
    icon: "chart",
    color: "navy"
  },
  {
    id: 7,
    name: "The Community",
    keywords: ["Connection", "Tribe", "Loyalty"],
    meaning: "Hands joined together form an unbreakable circle. Build a community around shared values and watch your brand advocates multiply organically.",
    reversed: "Isolation, transactional relationships, neglected audience",
    reflection: "Do your customers feel like they belong to something — or are they just buying from you?",
    activation: "Create one ritual (weekly thread, monthly event, shared challenge) that brings your people together.",
    icon: "hands",
    color: "teal"
  },
  {
    id: 8,
    name: "The Campaign",
    keywords: ["Strategy", "Execution", "Momentum"],
    meaning: "The flag flies high as you march forward. A well-planned campaign coordinates all elements toward a singular goal. Plant your flag and rally your forces.",
    reversed: "Scattered efforts, poor timing, lack of coordination",
    reflection: "Is your current campaign focused on one clear objective — or are you trying to do too much at once?",
    activation: "Define the single outcome your next campaign must achieve, and cut everything that doesn't serve it.",
    icon: "flag",
    color: "purple"
  },
  {
    id: 9,
    name: "The Competitor",
    keywords: ["Market", "Positioning", "Advantage"],
    meaning: "The chess knight moves in unexpected ways. Study your competition but forge your own path. True advantage comes from differentiation, not imitation.",
    reversed: "Obsessing over competition, losing focus, reactive strategy",
    reflection: "Are you building your strategy around your strengths — or reacting to what others are doing?",
    activation: "List three things you do that no competitor can replicate, and double down on one.",
    icon: "chess",
    color: "navy"
  },
  {
    id: 10,
    name: "The ROI",
    keywords: ["Results", "Growth", "Value"],
    meaning: "The golden coin represents the ultimate reward. Every investment should yield returns. Focus on value creation and the numbers will follow.",
    reversed: "Poor returns, wasted budget, unclear value proposition",
    reflection: "Where are you investing the most — and is it returning the most? What would you stop doing today if the numbers were honest?",
    activation: "Calculate the ROI of your top three activities and kill the lowest performer.",
    icon: "coin",
    color: "teal"
  },
  {
    id: 11,
    name: "The Vision",
    keywords: ["Clarity", "Direction", "Horizon"],
    meaning: "The spark before the strategy. The horizon you choose to walk toward. Clarity, direction, long-range intuition — the ability to see what others don't yet perceive.",
    reversed: "Fog, over-ideation, chasing too many futures, losing the thread",
    reflection: "What future am I designing toward? What becomes possible if I commit to one horizon?",
    activation: "Write a one-sentence 'North Star' that everything else must orbit.",
    icon: "compass",
    color: "purple"
  },
  {
    id: 12,
    name: "The Message",
    keywords: ["Precision", "Resonance", "Clarity"],
    meaning: "The truth you choose to speak into the world. Precision, resonance, emotional clarity — language that lands and moves people to action.",
    reversed: "Mixed signals, jargon, over-explaining, saying everything and nothing",
    reflection: "What is the simplest version of what I'm trying to say? What emotion do I want the audience to feel?",
    activation: "Rewrite your core message in 12 words or fewer.",
    icon: "scroll",
    color: "teal"
  },
  {
    id: 13,
    name: "The Channel",
    keywords: ["Medium", "Platform", "Distribution"],
    meaning: "The medium that carries your magic. Right place, right format, right timing. A channel that amplifies your voice and reaches those who need to hear it.",
    reversed: "Wrong platform, misaligned format, shouting into the void",
    reflection: "Where does my audience naturally listen? Which channel feels effortless for me?",
    activation: "Choose one channel to deepen instead of three to maintain.",
    icon: "broadcast",
    color: "navy"
  },
  {
    id: 14,
    name: "The Experience",
    keywords: ["Journey", "Emotion", "Immersion"],
    meaning: "The emotional journey you create — not just what people see, but what they feel. Immersion, delight, coherence, sensory intelligence.",
    reversed: "Friction, overwhelm, inconsistent touchpoints",
    reflection: "What emotion should someone leave with? Where does the experience currently break?",
    activation: "Map the first 3 minutes of your user journey and refine one moment.",
    icon: "sparkles",
    color: "orange"
  },
  {
    id: 15,
    name: "The Trust",
    keywords: ["Credibility", "Reliability", "Loyalty"],
    meaning: "The invisible currency of every brand relationship. Credibility, consistency, reliability — trust that is earned through kept promises.",
    reversed: "Doubt, broken promises, unclear boundaries",
    reflection: "What promise do I consistently keep? Where am I asking for trust without earning it?",
    activation: "Identify one trust-building ritual you can repeat weekly.",
    icon: "shield",
    color: "teal"
  },
  {
    id: 16,
    name: "The Insight",
    keywords: ["Truth", "Pattern", "Revelation"],
    meaning: "The truth beneath the data — the pattern behind the pattern. Clarity, revelation, strategic intuition, meaningful metrics that tell the real story.",
    reversed: "Vanity metrics, misinterpretation, analysis paralysis",
    reflection: "What is the data really telling me? What assumption needs to be challenged?",
    activation: "Turn one metric into a story instead of a number.",
    icon: "eye",
    color: "purple"
  },
  {
    id: 17,
    name: "The Ecosystem",
    keywords: ["Systems", "Collaboration", "Harmony"],
    meaning: "The interconnected world your brand lives within. Holistic systems, collaboration, multi-touch harmony where all parts work together.",
    reversed: "Silos, fragmentation, disconnected efforts",
    reflection: "What parts of my system aren't talking to each other? Who else benefits when I succeed?",
    activation: "Create a simple map of your ecosystem and identify one missing link.",
    icon: "network",
    color: "navy"
  },
  {
    id: 18,
    name: "The Momentum",
    keywords: ["Flow", "Energy", "Progress"],
    meaning: "The energy that builds when you move with intention. Flow, acceleration, compounding progress that creates unstoppable forward motion.",
    reversed: "Stagnation, burnout, inconsistent effort",
    reflection: "What small action creates the biggest shift? Where am I forcing instead of flowing?",
    activation: "Choose one repeatable action you can sustain for 30 days.",
    icon: "rocket",
    color: "orange"
  },
  {
    id: 19,
    name: "The Alignment",
    keywords: ["Integrity", "Coherence", "Harmony"],
    meaning: "When your values, actions, and audience finally click into place. Integrity, coherence, inner-outer harmony that feels authentic.",
    reversed: "Misalignment, self-betrayal, saying yes when you mean no",
    reflection: "What feels true but unspoken? What am I doing that no longer fits?",
    activation: "List three things to stop doing to stay aligned.",
    icon: "balance",
    color: "teal"
  },
  {
    id: 20,
    name: "The Algorithm",
    keywords: ["Optimization", "Discovery", "Adaptation"],
    meaning: "The unseen forces shaping visibility and discovery. Optimization, adaptability, strategic experimentation with the systems that govern reach.",
    reversed: "Chasing trends, over-optimization, losing your voice to the machine",
    reflection: "What does the algorithm reward that I can do authentically? What am I optimizing for?",
    activation: "Run one experiment for 7 days and observe the pattern.",
    icon: "code",
    color: "navy"
  },
  {
    id: 21,
    name: "The Path",
    keywords: ["Direction", "Purpose", "Destiny"],
    meaning: "The journey you choose — not the one chosen for you. Direction, purpose, intentional steps toward an unfolding destiny that feels alive.",
    reversed: "Detours, hesitation, fear of choosing wrong",
    reflection: "What path feels alive? What path feels expected?",
    activation: "Take one step today on the path that excites you most.",
    icon: "road",
    color: "purple"
  },
  {
    id: 22,
    name: "The Creative",
    keywords: ["Expression", "Originality", "Flow"],
    meaning: "The spark of originality that brings ideas into form. The force that turns imagination into something others can feel, see, or experience. Flow, expression, generative energy, authentic voice.",
    reversed: "Creative block, imitation, over-polish, fear of being seen",
    reflection: "What wants to be expressed through me? Where am I creating from fear instead of curiosity?",
    activation: "Create one thing today without editing or judging it.",
    icon: "palette",
    color: "orange"
  }
];

export const getRandomCard = (deck = MARKETING_TAROT_DECK) => {
  const randomIndex = Math.floor(Math.random() * deck.length);
  const card = deck[randomIndex];
  const isReversed = Math.random() > 0.5;
  return { ...card, isReversed };
};

export const shuffleDeck = (deck = MARKETING_TAROT_DECK) => {
  const shuffled = [...deck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};
